/**
 * INFINITY LIFE 2.0 — "Meu Negócio & Trabalho Autônomo" Component
 * Focado na gestão de home office, rotina comercial, clientes e produtividade autônoma
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';
import { getPriorityMeta, formatTimeBR } from '../utils/formatters.js';

let activeBusinessTab = 'tasks'; // 'tasks' | 'checklist' | 'clients'

export function renderBusinessView() {
  const { tasks, user } = store.get();

  // Filtrar apenas tarefas de trabalho profissional
  const workTasks = (tasks || []).filter((t) => t.is_work);
  const pendingWorkTasks = workTasks.filter((t) => t.status !== 'completed');
  const completedWorkTasks = workTasks.filter((t) => t.status === 'completed');

  // Recuperar checklist do expediente salvo no localStorage
  const todayKey = `infinity_work_day_${new Date().toISOString().split('T')[0]}`;
  let workDayState = {
    started: false,
    ended: false,
    checks: {
      inbox: false,
      priorities: false,
      followup: false,
      posture: false,
      review: false,
      backup: false,
    },
  };
  try {
    const saved = localStorage.getItem(todayKey);
    if (saved) workDayState = JSON.parse(saved);
  } catch (e) {
    // ignore
  }

  return `
    <div class="business-view">
      <!-- Cabeçalho do Negócio -->
      <div class="dashboard-header">
        <div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 6px;">
            <span style="font-size: 1.8rem;">💼</span>
            <h1 class="greeting-text">Central do Meu Negócio</h1>
          </div>
          <p class="date-text">Gestão profissional autônoma, clientes, frentes de trabalho e rotina comercial</p>
        </div>
        <div style="display: flex; gap: 10px;">
          <button class="btn-primary" id="btn-add-work-task">
            <span>＋</span> <span>Nova Tarefa Comercial</span>
          </button>
        </div>
      </div>

      <!-- Resumo Operacional em Cards -->
      <div class="metrics-grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-bottom: 24px;">
        <div class="metric-card">
          <div class="metric-label">Foco Comercial</div>
          <div class="metric-value" style="color: var(--primary);">${pendingWorkTasks.length}</div>
          <div class="metric-sub">Tarefas profissionais pendentes</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Expediente Hoje</div>
          <div class="metric-value" style="color: ${workDayState.started ? 'var(--emerald-accent)' : 'var(--amber-accent)'};">
            ${workDayState.ended ? 'Encerrado 🏁' : workDayState.started ? 'Em Andamento 🟢' : 'Aguardando Início ⏱️'}
          </div>
          <div class="metric-sub">${workDayState.started ? 'Produtividade ativa' : 'Pronto para começar'}</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Concluídas Hoje</div>
          <div class="metric-value" style="color: var(--emerald-accent);">${completedWorkTasks.length}</div>
          <div class="metric-sub">Entregas profissionais realizadas</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Rotina Comercial</div>
          <div class="metric-value" style="color: var(--cyan-accent);">100%</div>
          <div class="metric-sub">Isolamento seguro de dados</div>
        </div>
      </div>

      <!-- Abas da Central de Trabalho -->
      <div class="task-tabs" style="margin-bottom: 20px;">
        <button class="tab-btn ${activeBusinessTab === 'tasks' ? 'active' : ''}" data-tab="tasks">
          <span>📋</span> <span>Tarefas Profissionais (${pendingWorkTasks.length})</span>
        </button>
        <button class="tab-btn ${activeBusinessTab === 'checklist' ? 'active' : ''}" data-tab="checklist">
          <span>✅</span> <span>Checklist do Expediente</span>
        </button>
        <button class="tab-btn ${activeBusinessTab === 'clients' ? 'active' : ''}" data-tab="clients">
          <span>🤝</span> <span>Clientes & Propostas</span>
        </button>
      </div>

      <!-- Conteúdo das Abas -->
      <div class="tab-content-area">
        ${activeBusinessTab === 'tasks' ? renderWorkTasksTab(pendingWorkTasks, completedWorkTasks) : ''}
        ${activeBusinessTab === 'checklist' ? renderChecklistTab(workDayState) : ''}
        ${activeBusinessTab === 'clients' ? renderClientsTab() : ''}
      </div>
    </div>
  `;
}

function renderWorkTasksTab(pending, completed) {
  if (pending.length === 0 && completed.length === 0) {
    return `
      <div class="empty-state-card" style="text-align: center; padding: 50px 20px; background: var(--bg-card); border-radius: var(--radius-lg); border: 1px solid var(--border-subtle);">
        <div style="font-size: 2.5rem; margin-bottom: 12px;">💼</div>
        <h3 style="font-size: 1.2rem; margin-bottom: 6px;">Nenhuma tarefa comercial no momento</h3>
        <p style="color: var(--text-muted); font-size: 0.9rem; margin-bottom: 20px;">
          Cadastre propostas, entregas a clientes, reuniões e projetos profissionais.
        </p>
        <button class="btn-primary" id="btn-empty-add-work">
          <span>＋ Cadastrar Tarefa de Trabalho</span>
        </button>
      </div>
    `;
  }

  return `
    <div style="display: flex; flex-direction: column; gap: 12px;">
      ${pending.map((t) => renderWorkTaskCard(t)).join('')}

      ${completed.length > 0 ? `
        <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--border-subtle);">
          <h4 style="font-size: 0.9rem; color: var(--text-muted); margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.05em;">
            ✓ Entregas Concluídas (${completed.length})
          </h4>
          ${completed.map((t) => renderWorkTaskCard(t, true)).join('')}
        </div>
      ` : ''}
    </div>
  `;
}

function renderWorkTaskCard(task, isDone = false) {
  const pMeta = getPriorityMeta(task.priority);

  return `
    <div class="task-card ${isDone ? 'completed' : ''}" data-id="${task.id}" style="background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; gap: 14px;">
      <div style="display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0;">
        <button class="task-checkbox ${isDone ? 'checked' : ''}" data-action="toggle-task" data-id="${task.id}" title="${isDone ? 'Reabrir tarefa' : 'Concluir entrega'}">
          ${isDone ? '✓' : ''}
        </button>

        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; font-size: 0.95rem; color: ${isDone ? 'var(--text-muted)' : 'var(--text-primary)'}; text-decoration: ${isDone ? 'line-through' : 'none'}; word-break: break-word;">
            ${escapeHtml(task.title)}
          </div>

          <div style="display: flex; align-items: center; gap: 8px; font-size: 0.78rem; color: var(--text-muted); margin-top: 4px; flex-wrap: wrap;">
            <span class="badge" style="background: ${pMeta.bg}; color: ${pMeta.color}; font-size: 0.72rem; padding: 2px 8px; border-radius: var(--radius-full);">
              ${pMeta.label}
            </span>

            ${task.due_date ? `
              <span>📅 ${task.due_date}</span>
            ` : ''}

            ${task.context_location && task.context_location !== 'any' ? `
              <span>📍 ${task.context_location}</span>
            ` : ''}
          </div>
        </div>
      </div>

      <div style="display: flex; gap: 6px;">
        <button class="btn-icon" data-action="edit-task" data-id="${task.id}" title="Editar">
          ✎
        </button>
      </div>
    </div>
  `;
}

function renderChecklistTab(state) {
  return `
    <div style="background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: 24px; max-width: 680px;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--border-subtle);">
        <div>
          <h3 style="font-size: 1.15rem; font-weight: 700;">Rotina de Início e Encerramento</h3>
          <p style="font-size: 0.85rem; color: var(--text-muted); margin-top: 4px;">Garanta foco pleno no início e desconexão saudável no fim do expediente.</p>
        </div>
        <button class="btn-secondary" id="btn-toggle-expediente" style="font-size: 0.85rem;">
          ${state.started ? '🏁 Encerrar Dia' : '🚀 Iniciar Expediente'}
        </button>
      </div>

      <div style="display: flex; flex-direction: column; gap: 14px;">
        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-inbox" ${state.checks.inbox ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Zeramento de Caixa de Entrada & Mensagens</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Checar e-mails, WhatsApp de clientes e canais de comunicação com brevidade.</div>
          </div>
        </label>

        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-priorities" ${state.checks.priorities ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Definição das 3 Prioridades do Dia</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Escolher as 3 entregas que farão o dia valer a pena antes de abrir redes sociais.</div>
          </div>
        </label>

        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-followup" ${state.checks.followup ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Follow-up Comercial & Propostas</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Cobrar respostas de propostas enviadas e prospectar novas frentes de trabalho.</div>
          </div>
        </label>

        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-posture" ${state.checks.posture ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Pausa Ativa, Água e Postura</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Levantar a cada 60 minutos, alongar punhos e lombar, beber pelo menos 500ml de água.</div>
          </div>
        </label>

        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-review" ${state.checks.review ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Revisão de Entregas & Próximo Dia</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Marcar tarefas concluídas e planejar o roteiro de amanhã antes de desligar.</div>
          </div>
        </label>

        <label style="display: flex; align-items: center; gap: 12px; cursor: pointer; padding: 10px; border-radius: var(--radius-sm); background: var(--bg-tertiary);">
          <input type="checkbox" id="chk-backup" ${state.checks.backup ? 'checked' : ''} style="width: 18px; height: 18px; accent-color: var(--primary);" />
          <div>
            <div style="font-weight: 600; font-size: 0.9rem;">Backup & Desconexão Digital</div>
            <div style="font-size: 0.78rem; color: var(--text-muted);">Salvar arquivos, fechar abas de trabalho e desligar notificações de negócios.</div>
          </div>
        </label>
      </div>
    </div>
  `;
}

function renderClientsTab() {
  return `
    <div style="background: var(--bg-card); border: 1px solid var(--border-subtle); border-radius: var(--radius-lg); padding: 24px;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <div>
          <h3 style="font-size: 1.15rem; font-weight: 700;">Pipeline de Clientes & Projetos</h3>
          <p style="font-size: 0.85rem; color: var(--text-muted); margin-top: 4px;">Gestão de frentes de atuação e relacionamentos autônomos.</p>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px;">
        <div style="background: var(--bg-tertiary); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 18px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-weight: 700; color: var(--text-primary);">🎯 Prospecção Ativa</span>
            <span class="badge" style="background: rgba(99, 102, 241, 0.15); color: var(--primary); font-size: 0.72rem; padding: 2px 8px; border-radius: var(--radius-full);">Etapa 1</span>
          </div>
          <p style="font-size: 0.82rem; color: var(--text-secondary); line-height: 1.5;">Identificação de novos potenciais clientes e envio de apresentações de serviços.</p>
        </div>

        <div style="background: var(--bg-tertiary); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 18px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-weight: 700; color: var(--text-primary);">📄 Propostas em Negociação</span>
            <span class="badge" style="background: rgba(245, 158, 11, 0.15); color: var(--amber-accent); font-size: 0.72rem; padding: 2px 8px; border-radius: var(--radius-full);">Etapa 2</span>
          </div>
          <p style="font-size: 0.82rem; color: var(--text-secondary); line-height: 1.5;">Acompanhamento e reuniões de alinhamento para fechamento de contratos.</p>
        </div>

        <div style="background: var(--bg-tertiary); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 18px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-weight: 700; color: var(--text-primary);">🚀 Projetos em Execução</span>
            <span class="badge" style="background: rgba(16, 185, 129, 0.15); color: var(--emerald-accent); font-size: 0.72rem; padding: 2px 8px; border-radius: var(--radius-full);">Etapa 3</span>
          </div>
          <p style="font-size: 0.82rem; color: var(--text-secondary); line-height: 1.5;">Demandas em andamento com entregas e prazos monitorados pela agenda.</p>
        </div>
      </div>
    </div>
  `;
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachBusinessViewEvents(container) {
  // Troca de abas
  container.querySelectorAll('.tab-btn[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      activeBusinessTab = btn.dataset.tab;
      window.dispatchEvent(new CustomEvent('infinity:render'));
    });
  });

  // Botão Adicionar Tarefa de Trabalho
  const addBtn = container.querySelector('#btn-add-work-task') || container.querySelector('#btn-empty-add-work');
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      store.openTaskModal({ is_work: 1, priority: 'high' });
    });
  }

  // Toggle Task
  container.querySelectorAll('[data-action="toggle-task"]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.id;
      try {
        await api.toggleTask(id);
        window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Edit Task
  container.querySelectorAll('[data-action="edit-task"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const id = el.dataset.id;
      try {
        const res = await api.getTask(id);
        if (res.success) {
          store.openTaskModal(res.data);
        }
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Toggle Expediente
  const toggleExpBtn = container.querySelector('#btn-toggle-expediente');
  const todayKey = `infinity_work_day_${new Date().toISOString().split('T')[0]}`;
  if (toggleExpBtn) {
    toggleExpBtn.addEventListener('click', () => {
      let current = { started: false, ended: false, checks: {} };
      try {
        const s = localStorage.getItem(todayKey);
        if (s) current = JSON.parse(s);
      } catch (e) {}

      if (!current.started) {
        current.started = true;
        showToast('Expediente iniciado! Bom trabalho e mantenha o foco.', 'success');
      } else {
        current.ended = !current.ended;
        showToast(current.ended ? 'Expediente encerrado. Descanse com tranquilidade!' : 'Expediente reaberto.', 'info');
      }

      localStorage.setItem(todayKey, JSON.stringify(current));
      window.dispatchEvent(new CustomEvent('infinity:render'));
    });
  }

  // Checklist Checkboxes
  const checkKeys = ['inbox', 'priorities', 'followup', 'posture', 'review', 'backup'];
  checkKeys.forEach((key) => {
    const el = container.querySelector(`#chk-${key}`);
    if (el) {
      el.addEventListener('change', () => {
        let current = { started: false, ended: false, checks: {} };
        try {
          const s = localStorage.getItem(todayKey);
          if (s) current = JSON.parse(s);
        } catch (e) {}

        current.checks = current.checks || {};
        current.checks[key] = el.checked;
        if (el.checked) current.started = true;

        localStorage.setItem(todayKey, JSON.stringify(current));
        showToast('Item do expediente atualizado.', 'info');
      });
    }
  });
}
